/**
 * Analytics utility functions for lead/deal analysis
 */

import type { Lead, Deal, LeadStage } from '@/types';
import type { StageTransition } from '@/services/api/monday';

// Lead funnel stages in order (top to bottom)
export const LEAD_STAGE_ORDER: LeadStage[] = [
  'Identified',
  'Contacted',
  'Scheduled',
  'Account Qualification',
  'Qualified',
  'Project Identification',
];

// ============================================================================
// Types
// ============================================================================

export interface StageVelocity {
  stage: string;
  avgDays: number;
  count: number;
}

export interface ConversionMetrics {
  stage: string;
  count: number;
  conversionRate: number; // Rate to next stage (0-100)
  cumulativeRate: number; // Rate from first stage (0-100)
}

export interface AgingBucket {
  label: string;
  min: number;
  max: number;
  count: number;
  leadIds: string[]; // IDs of leads in this bucket
}

export interface LeadAgingData {
  stage: string;
  total: number;
  leadIds: string[]; // All lead IDs that were historically in this stage
  buckets: AgingBucket[];
}

export interface TrendDataPoint {
  date: string;
  newLeads: number;
  stageChanges: number;
  dealsCreated: number;
}

// ============================================================================
// Stage Velocity
// ============================================================================

/**
 * Calculate average time (days) leads spend in each stage
 *
 * ⚠️ LIMITATION: This basic calculation uses createdAt to lastActivity as a proxy,
 * which measures total time since creation, not actual time spent in each stage.
 *
 * For accurate per-stage velocity, use `calculateUnifiedHistoricalAnalytics()` instead,
 * which uses actual stage transition timestamps from Monday.com activity logs.
 *
 * This function is provided as a fallback when transition data is unavailable.
 */
export function calculateStageVelocity(leads: Lead[], deals: Deal[]): StageVelocity[] {
  const stageStats: Record<string, { totalDays: number; count: number }> = {};

  // For leads, calculate days from createdAt to lastActivity
  for (const lead of leads) {
    const stage = lead.stage;
    if (!stage) continue;

    const created = new Date(lead.createdAt);
    const lastActivity = lead.lastActivity ? new Date(lead.lastActivity) : new Date();

    if (isNaN(created.getTime())) continue;

    const days = Math.max(0, Math.floor((lastActivity.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));

    if (!stageStats[stage]) {
      stageStats[stage] = { totalDays: 0, count: 0 };
    }
    stageStats[stage].totalDays += days;
    stageStats[stage].count += 1;
  }

  // For deals, add their stage stats
  for (const deal of deals) {
    const stage = deal.stage;
    if (!stage) continue;

    const created = new Date(deal.createdAt);
    const updated = deal.updatedAt ? new Date(deal.updatedAt) : new Date();

    if (isNaN(created.getTime())) continue;

    const days = Math.max(0, Math.floor((updated.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));

    const dealStage = `Deal: ${stage}`;
    if (!stageStats[dealStage]) {
      stageStats[dealStage] = { totalDays: 0, count: 0 };
    }
    stageStats[dealStage].totalDays += days;
    stageStats[dealStage].count += 1;
  }

  // Convert to array and sort by lead stage order
  const result: StageVelocity[] = [];

  // Add lead stages in funnel order first
  for (const stage of LEAD_STAGE_ORDER) {
    if (stageStats[stage]) {
      result.push({
        stage,
        avgDays: Math.round(stageStats[stage].totalDays / stageStats[stage].count),
        count: stageStats[stage].count,
      });
    }
  }

  // Add any remaining stages (including deal stages)
  for (const [stage, stats] of Object.entries(stageStats)) {
    if (!LEAD_STAGE_ORDER.includes(stage as LeadStage)) {
      result.push({
        stage,
        avgDays: Math.round(stats.totalDays / stats.count),
        count: stats.count,
      });
    }
  }

  return result;
}

// ============================================================================
// Conversion Funnel
// ============================================================================

/**
 * Calculate conversion funnel metrics for leads
 * Shows count at each stage and conversion rates
 */
export function calculateConversionFunnel(leads: Lead[]): ConversionMetrics[] {
  // Count leads at each stage
  const stageCounts: Record<string, number> = {};
  for (const lead of leads) {
    const stage = lead.stage || 'Unknown';
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
  }

  // Build funnel in stage order
  const funnel: ConversionMetrics[] = [];
  let previousCount = 0;
  const firstStage = LEAD_STAGE_ORDER[0];
  const firstStageCount = firstStage ? (stageCounts[firstStage] || 0) : 0;

  for (const stage of LEAD_STAGE_ORDER) {
    const count = stageCounts[stage] || 0;

    // Conversion rate: what % moved to this stage from previous
    // For first stage, it's 100% (entry point)
    let conversionRate = 100;
    if (funnel.length > 0 && previousCount > 0) {
      conversionRate = Math.round((count / previousCount) * 100);
    }

    // Cumulative rate: what % of original leads made it here
    const cumulativeRate = firstStageCount > 0 ? Math.round((count / firstStageCount) * 100) : 0;

    funnel.push({
      stage,
      count,
      conversionRate,
      cumulativeRate: funnel.length === 0 ? 100 : cumulativeRate,
    });

    previousCount = count;
  }

  return funnel;
}

// ============================================================================
// Lead Aging
// ============================================================================

const AGING_BUCKETS = [
  { label: '< 7 days', min: 0, max: 7 },
  { label: '7-14 days', min: 7, max: 14 },
  { label: '14-30 days', min: 14, max: 30 },
  { label: '> 30 days', min: 30, max: Infinity },
];

/**
 * Calculate lead aging - how long leads have been in each stage
 * Helps identify stuck leads that need attention
 */
export function calculateLeadAging(leads: Lead[]): LeadAgingData[] {
  const now = new Date();
  const stageAging: Record<string, {
    total: number;
    leadIds: Set<string>;
    buckets: number[];
    bucketLeadIds: Set<string>[];
  }> = {};

  for (const lead of leads) {
    const stage = lead.stage || 'Unknown';
    const lastActivity = lead.lastActivity ? new Date(lead.lastActivity) : new Date(lead.createdAt);

    if (isNaN(lastActivity.getTime())) continue;

    const daysInStage = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

    if (!stageAging[stage]) {
      stageAging[stage] = {
        total: 0,
        leadIds: new Set(),
        buckets: [0, 0, 0, 0],
        bucketLeadIds: [new Set(), new Set(), new Set(), new Set()]
      };
    }

    stageAging[stage].total += 1;
    stageAging[stage].leadIds.add(lead.id);

    // Find which bucket this lead falls into
    for (let i = 0; i < AGING_BUCKETS.length; i++) {
      const bucket = AGING_BUCKETS[i];
      const stageData = stageAging[stage];
      if (bucket && stageData && daysInStage >= bucket.min && daysInStage < bucket.max) {
        stageData.buckets[i] = (stageData.buckets[i] ?? 0) + 1;
        stageData.bucketLeadIds[i]?.add(lead.id);
        break;
      }
    }
  }

  // Convert to array in stage order
  const result: LeadAgingData[] = [];

  for (const stage of LEAD_STAGE_ORDER) {
    const stageData = stageAging[stage];
    if (stageData) {
      result.push({
        stage,
        total: stageData.total,
        leadIds: Array.from(stageData.leadIds),
        buckets: AGING_BUCKETS.map((bucket, i) => ({
          ...bucket,
          count: stageData.buckets[i] ?? 0,
          leadIds: Array.from(stageData.bucketLeadIds[i] ?? new Set()),
        })),
      });
    }
  }

  // Add any stages not in the standard order
  for (const [stage, data] of Object.entries(stageAging)) {
    if (!LEAD_STAGE_ORDER.includes(stage as LeadStage)) {
      result.push({
        stage,
        total: data.total,
        leadIds: Array.from(data.leadIds),
        buckets: AGING_BUCKETS.map((bucket, i) => ({
          ...bucket,
          count: data.buckets[i] ?? 0,
          leadIds: Array.from(data.bucketLeadIds[i] ?? new Set()),
        })),
      });
    }
  }

  return result;
}

// ============================================================================
// Activity Trends
// ============================================================================

/**
 * Calculate activity trends over time
 * Shows new leads, stage changes, and deals created per day
 */
export function calculateActivityTrends(
  leads: Lead[],
  deals: Deal[],
  days: number = 30
): TrendDataPoint[] {
  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  // Initialize data points for each day
  const dataByDate: Record<string, TrendDataPoint> = {};
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split('T')[0] ?? '';
    if (dateStr) {
      dataByDate[dateStr] = {
        date: dateStr,
        newLeads: 0,
        stageChanges: 0,
        dealsCreated: 0,
      };
    }
  }

  // Count new leads by creation date
  for (const lead of leads) {
    const createdDate = lead.createdAt?.split('T')[0];
    if (createdDate && dataByDate[createdDate]) {
      dataByDate[createdDate].newLeads += 1;
    }

    // Use lastActivity as proxy for stage changes
    const activityDate = lead.lastActivity?.split('T')[0];
    if (activityDate && dataByDate[activityDate] && activityDate !== createdDate) {
      dataByDate[activityDate].stageChanges += 1;
    }
  }

  // Count deals by creation date
  for (const deal of deals) {
    const createdDate = deal.createdAt?.split('T')[0];
    if (createdDate && dataByDate[createdDate]) {
      dataByDate[createdDate].dealsCreated += 1;
    }
  }

  // Convert to sorted array
  return Object.values(dataByDate).sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================================================
// Summary Stats
// ============================================================================

export interface AnalyticsSummary {
  totalLeads: number;
  totalDeals: number;
  avgVelocityDays: number;
  overallConversionRate: number;
  leadsNeedingAttention: number;
}

/**
 * Calculate summary analytics for dashboard header
 */
export function calculateAnalyticsSummary(
  leads: Lead[],
  deals: Deal[],
  velocity: StageVelocity[],
  funnel: ConversionMetrics[],
  aging: LeadAgingData[]
): AnalyticsSummary {
  // Average velocity across all stages
  const totalVelocity = velocity.reduce((sum, v) => sum + v.avgDays * v.count, 0);
  const totalVelocityCount = velocity.reduce((sum, v) => sum + v.count, 0);
  const avgVelocityDays = totalVelocityCount > 0 ? Math.round(totalVelocity / totalVelocityCount) : 0;

  // Overall conversion: last stage / first stage
  const lastFunnelStage = funnel[funnel.length - 1];
  const overallConversionRate = lastFunnelStage?.cumulativeRate ?? 0;

  // Leads stuck > 30 days
  const leadsNeedingAttention = aging.reduce((sum, stage) => {
    const stuckBucket = stage.buckets.find((b) => b.min >= 30);
    return sum + (stuckBucket?.count ?? 0);
  }, 0);

  return {
    totalLeads: leads.length,
    totalDeals: deals.length,
    avgVelocityDays,
    overallConversionRate,
    leadsNeedingAttention,
  };
}

// ============================================================================
// Lead-to-Deal Conversion Tracking
// ============================================================================

export interface ConvertedLead {
  lead: Lead;
  deal: Deal;
  daysToConvert: number;
  leadStageAtConversion: string;
}

export interface LeadToDealConversion {
  convertedLeads: ConvertedLead[];
  totalLeads: number;
  totalDeals: number;
  convertedCount: number;
  conversionRate: number;
  avgDaysToConvert: number;
  conversionsByLeadStage: Record<string, number>;
  conversionsByDealStage: Record<string, number>;
}

/**
 * Normalize company name for matching - removes common suffixes and punctuation
 */
export function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Remove common company suffixes
    .replace(/\b(inc\.?|incorporated|llc\.?|ltd\.?|limited|corp\.?|corporation|co\.?|company|plc\.?|gmbh|ag|sa|bv|nv|pty\.?)\b/gi, '')
    // Remove punctuation
    .replace(/[.,\-_'"()]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract words from company name for fuzzy matching
 */
function extractCompanyWords(name: string): Set<string> {
  const normalized = normalizeCompanyName(name);
  const words = normalized.split(/\s+/).filter(w => w.length > 2);
  return new Set(words);
}

/**
 * Calculate similarity score between two company names (0-1)
 */
function companyNameSimilarity(name1: string, name2: string): number {
  const norm1 = normalizeCompanyName(name1);
  const norm2 = normalizeCompanyName(name2);

  // Exact match after normalization
  if (norm1 === norm2) return 1.0;

  // One contains the other
  if (norm1.includes(norm2) || norm2.includes(norm1)) return 0.9;

  // Word overlap
  const words1 = extractCompanyWords(name1);
  const words2 = extractCompanyWords(name2);

  if (words1.size === 0 || words2.size === 0) return 0;

  // Count overlapping words
  let overlap = 0;
  for (const word of words1) {
    if (words2.has(word)) overlap++;
  }

  // Jaccard similarity
  const union = new Set([...words1, ...words2]).size;
  const similarity = overlap / union;

  // Boost if first word matches (often the main company name)
  const firstWord1 = [...words1][0];
  const firstWord2 = [...words2][0];
  if (firstWord1 && firstWord2 && firstWord1 === firstWord2) {
    return Math.min(1.0, similarity + 0.2);
  }

  return similarity;
}

/**
 * Match leads to deals based on company name or leadId
 * Tracks which leads have converted to deals and calculates conversion metrics
 * Uses fuzzy matching to handle variations in company names
 */
export function calculateLeadToDealConversion(
  leads: Lead[],
  deals: Deal[]
): LeadToDealConversion {
  const convertedLeads: ConvertedLead[] = [];
  const conversionsByLeadStage: Record<string, number> = {};
  const conversionsByDealStage: Record<string, number> = {};
  const matchedLeadIds = new Set<string>();

  // Create lookup maps
  const leadsById = new Map<string, Lead>();
  const leadsByNormalizedCompany = new Map<string, Lead>();
  const leadsByDomain = new Map<string, Lead>();

  for (const lead of leads) {
    leadsById.set(lead.id, lead);

    // Index by normalized company name
    const normalizedCompany = normalizeCompanyName(lead.company);
    if (normalizedCompany && !leadsByNormalizedCompany.has(normalizedCompany)) {
      leadsByNormalizedCompany.set(normalizedCompany, lead);
    }

    // Index by email domain
    if (lead.contactEmail) {
      const domain = lead.contactEmail.split('@')[1]?.toLowerCase();
      if (domain && !leadsByDomain.has(domain)) {
        leadsByDomain.set(domain, lead);
      }
    }

    // Also index by website domain from Apollo data
    if (lead.apolloData?.website) {
      try {
        const url = lead.apolloData.website.startsWith('http')
          ? lead.apolloData.website
          : `https://${lead.apolloData.website}`;
        const domain = new URL(url).hostname.replace('www.', '').toLowerCase();
        if (!leadsByDomain.has(domain)) {
          leadsByDomain.set(domain, lead);
        }
      } catch {
        // Invalid URL, skip
      }
    }
  }

  // Match deals to leads
  for (const deal of deals) {
    let matchedLead: Lead | undefined;

    // 1. Try to match by leadId first (direct link)
    if (deal.leadId) {
      matchedLead = leadsById.get(deal.leadId);
    }

    // 2. Try exact normalized company match
    if (!matchedLead) {
      const normalizedDealCompany = normalizeCompanyName(deal.company);
      matchedLead = leadsByNormalizedCompany.get(normalizedDealCompany);

      // Also try deal name
      if (!matchedLead) {
        const normalizedDealName = normalizeCompanyName(deal.name);
        matchedLead = leadsByNormalizedCompany.get(normalizedDealName);
      }
    }

    // 3. Try fuzzy company name matching
    if (!matchedLead) {
      let bestMatch: Lead | undefined;
      let bestScore = 0;
      const SIMILARITY_THRESHOLD = 0.6;

      for (const lead of leads) {
        // Skip already matched leads
        if (matchedLeadIds.has(lead.id)) continue;

        // Try both deal.company and deal.name
        const scoreCompany = companyNameSimilarity(deal.company, lead.company);
        const scoreName = companyNameSimilarity(deal.name, lead.company);
        const score = Math.max(scoreCompany, scoreName);

        if (score > bestScore && score >= SIMILARITY_THRESHOLD) {
          bestScore = score;
          bestMatch = lead;
        }
      }

      if (bestMatch) {
        matchedLead = bestMatch;
      }
    }

    // 4. Try domain matching if we have Apollo data
    if (!matchedLead && deal.company) {
      // Try to extract domain from company name (sometimes company is actually a domain)
      const companyAsDomain = deal.company.toLowerCase().replace(/\s+/g, '');
      if (companyAsDomain.includes('.')) {
        const domain = companyAsDomain.replace('www.', '');
        matchedLead = leadsByDomain.get(domain);
      }
    }

    if (matchedLead && !matchedLeadIds.has(matchedLead.id)) {
      // Skip "Closed Lost" deals - they shouldn't count as successful conversions
      if (isLostStage(deal.stage)) {
        continue;
      }

      matchedLeadIds.add(matchedLead.id);

      // Calculate days to convert
      const leadCreated = new Date(matchedLead.createdAt);
      const dealCreated = new Date(deal.createdAt);
      const daysToConvert = Math.max(
        0,
        Math.floor((dealCreated.getTime() - leadCreated.getTime()) / (1000 * 60 * 60 * 24))
      );

      convertedLeads.push({
        lead: matchedLead,
        deal,
        daysToConvert,
        leadStageAtConversion: matchedLead.stage,
      });

      // Track by lead stage at conversion
      const leadStage = matchedLead.stage || 'Unknown';
      conversionsByLeadStage[leadStage] = (conversionsByLeadStage[leadStage] || 0) + 1;

      // Track by deal stage
      const dealStage = deal.stage || 'Unknown';
      conversionsByDealStage[dealStage] = (conversionsByDealStage[dealStage] || 0) + 1;
    }
  }

  // Calculate metrics
  const convertedCount = convertedLeads.length;
  const conversionRate = leads.length > 0 ? Math.round((convertedCount / leads.length) * 100) : 0;
  const avgDaysToConvert =
    convertedCount > 0
      ? Math.round(convertedLeads.reduce((sum, c) => sum + c.daysToConvert, 0) / convertedCount)
      : 0;

  return {
    convertedLeads,
    totalLeads: leads.length,
    totalDeals: deals.length,
    convertedCount,
    conversionRate,
    avgDaysToConvert,
    conversionsByLeadStage,
    conversionsByDealStage,
  };
}

/**
 * Get the full funnel from lead stages through deal stages
 */
export interface FullFunnelStage {
  stage: string;
  type: 'lead' | 'deal';
  count: number;
  isConversionPoint?: boolean;
}

export function calculateFullFunnel(leads: Lead[], deals: Deal[]): FullFunnelStage[] {
  const funnel: FullFunnelStage[] = [];

  // Add lead stages
  const leadStageCounts: Record<string, number> = {};
  for (const lead of leads) {
    const stage = lead.stage || 'Unknown';
    leadStageCounts[stage] = (leadStageCounts[stage] || 0) + 1;
  }

  for (const stage of LEAD_STAGE_ORDER) {
    if (leadStageCounts[stage]) {
      funnel.push({
        stage,
        type: 'lead',
        count: leadStageCounts[stage],
      });
    }
  }

  // Add conversion point
  funnel.push({
    stage: 'Converted to Deal',
    type: 'lead',
    count: deals.length,
    isConversionPoint: true,
  });

  // Add deal stages
  const dealStageCounts: Record<string, number> = {};
  for (const deal of deals) {
    const stage = deal.stage || 'Unknown';
    dealStageCounts[stage] = (dealStageCounts[stage] || 0) + 1;
  }

  const dealStageOrder = ['New', 'Discovery', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
  for (const stage of dealStageOrder) {
    if (dealStageCounts[stage]) {
      funnel.push({
        stage,
        type: 'deal',
        count: dealStageCounts[stage],
      });
    }
  }

  return funnel;
}

// ============================================================================
// Historical Funnel Analysis
// ============================================================================

export interface HistoricalFunnelStage {
  stage: string;
  passedThrough: number; // Total leads that ever reached this stage
  passedThroughIds: string[]; // IDs of leads that passed through this stage
  currentlyIn: number; // Leads currently in this stage
  exitedTo: Record<string, number>; // Where leads went after this stage
  avgDaysInStage: number;
  conversionRate: number; // % that moved to next stage
  dropoffRate: number; // % that dropped off (didn't progress)
}

export interface HistoricalFunnelData {
  stages: HistoricalFunnelStage[];
  totalLeadsInPeriod: number;
  overallConversionRate: number;
  leadsInProgress: number;
}

/**
 * Calculate historical funnel showing how leads moved through stages over time.
 * Uses activity logs (stage transitions) to track the journey of ALL leads,
 * not just their current state.
 *
 * @param leads - All leads in the system
 * @param transitions - Stage transitions from activity logs
 * @param startDate - Optional start date filter
 * @param endDate - Optional end date filter
 */
export function calculateHistoricalFunnel(
  leads: Lead[],
  transitions: StageTransition[],
  startDate?: string,
  endDate?: string
): HistoricalFunnelData {
  // Filter transitions by date range (when the transition happened)
  let filteredTransitions = transitions;
  if (startDate || endDate) {
    filteredTransitions = transitions.filter((t) => {
      const transitionDate = t.timestamp?.split('T')[0] ?? '';
      if (!transitionDate) return true;
      if (startDate && transitionDate < startDate) return false;
      if (endDate && transitionDate > endDate) return false;
      return true;
    });
  }

  // Get set of lead IDs that had transitions in the date range
  const leadsWithTransitions = new Set(filteredTransitions.map(t => t.itemId));

  // Include leads that either:
  // 1. Had transitions during the date range, OR
  // 2. Were created during the date range (to capture leads with no transitions yet)
  let filteredLeads = leads;
  if (startDate || endDate) {
    filteredLeads = leads.filter((lead) => {
      // Include if lead had transitions in the date range
      if (leadsWithTransitions.has(lead.id)) return true;

      // Also include if lead was created in the date range
      const createdDate = lead.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (startDate && createdDate < startDate) return false;
      if (endDate && createdDate > endDate) return false;
      return true;
    });
  }

  // Build a map of lead histories: itemId -> list of stages visited
  const leadHistories = new Map<string, { stages: string[]; timestamps: string[]; currentStage: string }>();

  // Create a map of lead IDs to their current state (for later reference)
  const leadCurrentState = new Map<string, { stage: string; createdAt: string }>();
  for (const lead of filteredLeads) {
    leadCurrentState.set(lead.id, { stage: lead.stage, createdAt: lead.createdAt });
  }

  // Process transitions to build complete history (oldest to newest)
  // Don't pre-initialize with current stage - build from transitions first
  const sortedTransitions = [...filteredTransitions].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  for (const transition of sortedTransitions) {
    const history = leadHistories.get(transition.itemId);

    if (history) {
      // Add fromStage if we don't have it yet (could be initial stage)
      if (transition.fromStage && !history.stages.includes(transition.fromStage)) {
        // Insert at beginning if it's earlier in the funnel
        const fromIndex = LEAD_STAGE_ORDER.indexOf(transition.fromStage as LeadStage);
        const firstStageIndex = LEAD_STAGE_ORDER.indexOf(history.stages[0] as LeadStage);
        if (fromIndex !== -1 && (firstStageIndex === -1 || fromIndex < firstStageIndex)) {
          history.stages.unshift(transition.fromStage);
          history.timestamps.unshift(transition.timestamp);
        }
      }

      // Add toStage if not already present
      if (!history.stages.includes(transition.toStage)) {
        history.stages.push(transition.toStage);
        history.timestamps.push(transition.timestamp);
      }
    } else {
      // New lead from transitions
      const stages: string[] = [];
      const timestamps: string[] = [];
      if (transition.fromStage) {
        stages.push(transition.fromStage);
        timestamps.push(transition.timestamp);
      }
      stages.push(transition.toStage);
      timestamps.push(transition.timestamp);

      leadHistories.set(transition.itemId, {
        stages,
        timestamps,
        currentStage: transition.toStage, // Will be updated as we process more
      });
    }

    // Update current stage for this lead
    const existingHistory = leadHistories.get(transition.itemId);
    if (existingHistory) {
      existingHistory.currentStage = transition.toStage;
    }
  }

  // Now add any leads that had NO transitions (only current state)
  // For these leads, infer they passed through all stages up to their current stage
  for (const lead of filteredLeads) {
    if (!leadHistories.has(lead.id)) {

      // Find the current stage's position in the funnel
      const currentStageIndex = LEAD_STAGE_ORDER.indexOf(lead.stage as LeadStage);

      // Infer the journey: assume lead passed through all stages up to current
      const inferredStages: string[] = [];
      const inferredTimestamps: string[] = [];

      if (currentStageIndex >= 0) {
        // Add all stages from the beginning up to and including current stage
        for (let i = 0; i <= currentStageIndex; i++) {
          const stage = LEAD_STAGE_ORDER[i];
          if (stage) {
            inferredStages.push(stage);
            // Use created date as timestamp (we don't know actual transition times)
            inferredTimestamps.push(lead.createdAt);
          }
        }
      } else {
        // Stage not in standard order, just use current stage
        inferredStages.push(lead.stage);
        inferredTimestamps.push(lead.createdAt);
      }

      leadHistories.set(lead.id, {
        stages: inferredStages,
        timestamps: inferredTimestamps,
        currentStage: lead.stage,
      });
    }
  }

  // Calculate stage metrics
  const stageMetrics: Record<string, {
    passedThrough: Set<string>;
    currentlyIn: number;
    exitedTo: Record<string, number>;
    totalDays: number;
    daysCount: number;
  }> = {};

  // Initialize metrics for all stages in order
  for (const stage of LEAD_STAGE_ORDER) {
    stageMetrics[stage] = {
      passedThrough: new Set(),
      currentlyIn: 0,
      exitedTo: {},
      totalDays: 0,
      daysCount: 0,
    };
  }

  // Process each lead's history
  for (const [leadId, history] of leadHistories) {
    const { stages, timestamps } = history;

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!stage) continue;

      const metrics = stageMetrics[stage];
      if (!metrics) {
        stageMetrics[stage] = {
          passedThrough: new Set(),
          currentlyIn: 0,
          exitedTo: {},
          totalDays: 0,
          daysCount: 0,
        };
      }

      const stageData = stageMetrics[stage];
      if (!stageData) continue;

      // Mark this lead as having passed through this stage
      stageData.passedThrough.add(leadId);

      // Calculate days in stage
      const timestampEntry = timestamps[i];
      if (timestampEntry) {
        const entryDate = new Date(timestampEntry);
        const timestampExit = timestamps[i + 1];
        const exitDate = timestampExit ? new Date(timestampExit) : new Date();
        const days = Math.max(0, Math.floor((exitDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24)));
        stageData.totalDays += days;
        stageData.daysCount += 1;
      }

      // Track where they went next
      if (i < stages.length - 1) {
        const nextStage = stages[i + 1];
        if (nextStage) {
          stageData.exitedTo[nextStage] = (stageData.exitedTo[nextStage] || 0) + 1;
        }
      }
    }

    // Count currently in stage (last stage in history)
    const currentStage = stages[stages.length - 1];
    if (currentStage && stageMetrics[currentStage]) {
      stageMetrics[currentStage].currentlyIn += 1;
    }
  }

  // Build funnel data
  const stages: HistoricalFunnelStage[] = [];

  for (const stage of LEAD_STAGE_ORDER) {
    const metrics = stageMetrics[stage];
    if (!metrics) continue;

    const passedThrough = metrics.passedThrough.size;
    const avgDays = metrics.daysCount > 0 ? Math.round(metrics.totalDays / metrics.daysCount) : 0;

    // Calculate conversion rate (% that moved to next stage)
    const nextStageIndex = LEAD_STAGE_ORDER.indexOf(stage as LeadStage) + 1;
    const nextStage = LEAD_STAGE_ORDER[nextStageIndex];
    const movedToNext = nextStage ? (metrics.exitedTo[nextStage] || 0) : 0;
    const conversionRate = passedThrough > 0 ? Math.round((movedToNext / passedThrough) * 100) : 0;

    // Calculate dropoff rate (leads that didn't progress and aren't currently in stage)
    const dropoffRate = passedThrough > 0
      ? Math.round(((passedThrough - movedToNext - metrics.currentlyIn) / passedThrough) * 100)
      : 0;

    stages.push({
      stage,
      passedThrough,
      passedThroughIds: Array.from(metrics.passedThrough),
      currentlyIn: metrics.currentlyIn,
      exitedTo: metrics.exitedTo,
      avgDaysInStage: avgDays,
      conversionRate,
      dropoffRate: Math.max(0, dropoffRate),
    });
  }

  // Calculate overall metrics
  const totalLeadsInPeriod = leadHistories.size;
  const firstStageData = stages[0];
  const lastStageData = stages[stages.length - 1];
  const firstStageCount = firstStageData?.passedThrough ?? 0;
  const lastStageCount = lastStageData?.passedThrough ?? 0;
  const overallConversionRate = firstStageCount > 0
    ? Math.round((lastStageCount / firstStageCount) * 100)
    : 0;

  const leadsInProgress = stages.reduce((sum, s) => sum + s.currentlyIn, 0);

  const result = {
    stages,
    totalLeadsInPeriod,
    overallConversionRate,
    leadsInProgress,
  };

  return result;
}

/**
 * Convert historical funnel data to ConversionMetrics format for compatibility
 * with existing ConversionFunnelChart component
 */
export function historicalFunnelToConversionMetrics(
  historicalData: HistoricalFunnelData
): ConversionMetrics[] {
  const firstStageCount = historicalData.stages[0]?.passedThrough ?? 1;

  return historicalData.stages.map((stage, index) => {
    const previousStage = historicalData.stages[index - 1];
    const previousCount = previousStage?.passedThrough ?? stage.passedThrough;

    return {
      stage: stage.stage,
      count: stage.passedThrough,
      conversionRate: index === 0 ? 100 : (previousCount > 0
        ? Math.round((stage.passedThrough / previousCount) * 100)
        : 0),
      cumulativeRate: firstStageCount > 0
        ? Math.round((stage.passedThrough / firstStageCount) * 100)
        : 0,
    };
  });
}

// ============================================================================
// Unified Historical Analytics
// ============================================================================

/**
 * Unified analytics result - calculated once from transitions, used by all panels
 */
export interface UnifiedHistoricalAnalytics {
  funnel: HistoricalFunnelData;
  stageVelocity: StageVelocity[];
  leadAging: LeadAgingData[];
  activityTrends: TrendDataPoint[];
  leadToDealConversion: LeadToDealConversion;
  // Metadata
  totalLeads: number;
  totalTransitions: number;
  dateRange: { start: string; end: string } | null;
}

/**
 * Calculate all historical analytics in a single pass.
 * This is more efficient than calling individual functions as it processes
 * transitions and builds lead histories only once.
 */
export function calculateUnifiedHistoricalAnalytics(
  leads: Lead[],
  deals: Deal[],
  transitions: StageTransition[],
  startDate?: string,
  endDate?: string
): UnifiedHistoricalAnalytics {
  // Filter transitions by date range
  let filteredTransitions = transitions;
  if (startDate || endDate) {
    filteredTransitions = transitions.filter((t) => {
      const transitionDate = t.timestamp?.split('T')[0] ?? '';
      if (!transitionDate) return true;
      if (startDate && transitionDate < startDate) return false;
      if (endDate && transitionDate > endDate) return false;
      return true;
    });
  }

  // Get lead IDs with transitions in date range
  const leadsWithTransitions = new Set(filteredTransitions.map(t => t.itemId));

  // Filter leads: include those with transitions OR created in date range
  let filteredLeads = leads;
  if (startDate || endDate) {
    filteredLeads = leads.filter((lead) => {
      if (leadsWithTransitions.has(lead.id)) return true;
      const createdDate = lead.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (startDate && createdDate < startDate) return false;
      if (endDate && createdDate > endDate) return false;
      return true;
    });
  }

  // Filter deals by date range
  let filteredDeals = deals;
  if (startDate || endDate) {
    filteredDeals = deals.filter((deal) => {
      const createdDate = deal.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (startDate && createdDate < startDate) return false;
      if (endDate && createdDate > endDate) return false;
      return true;
    });
  }

  // Build lead histories from transitions (sorted oldest to newest)
  const sortedTransitions = [...filteredTransitions].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Lead history: stages visited with timestamps
  interface LeadHistory {
    stages: string[];
    timestamps: string[];
    currentStage: string;
    stageEntryTime: Record<string, string>; // When lead entered each stage
  }

  const leadHistories = new Map<string, LeadHistory>();

  // Process transitions to build histories
  for (const transition of sortedTransitions) {
    const history = leadHistories.get(transition.itemId);

    if (history) {
      // Add fromStage if not present
      if (transition.fromStage && !history.stages.includes(transition.fromStage)) {
        const fromIndex = LEAD_STAGE_ORDER.indexOf(transition.fromStage as LeadStage);
        const firstIndex = LEAD_STAGE_ORDER.indexOf(history.stages[0] as LeadStage);
        if (fromIndex !== -1 && (firstIndex === -1 || fromIndex < firstIndex)) {
          history.stages.unshift(transition.fromStage);
          history.timestamps.unshift(transition.timestamp);
          history.stageEntryTime[transition.fromStage] = transition.timestamp;
        }
      }
      // Add toStage if not present
      if (!history.stages.includes(transition.toStage)) {
        history.stages.push(transition.toStage);
        history.timestamps.push(transition.timestamp);
        history.stageEntryTime[transition.toStage] = transition.timestamp;
      }
      history.currentStage = transition.toStage;
    } else {
      const stages: string[] = [];
      const timestamps: string[] = [];
      const stageEntryTime: Record<string, string> = {};

      if (transition.fromStage) {
        stages.push(transition.fromStage);
        timestamps.push(transition.timestamp);
        stageEntryTime[transition.fromStage] = transition.timestamp;
      }
      stages.push(transition.toStage);
      timestamps.push(transition.timestamp);
      stageEntryTime[transition.toStage] = transition.timestamp;

      leadHistories.set(transition.itemId, {
        stages,
        timestamps,
        currentStage: transition.toStage,
        stageEntryTime,
      });
    }
  }

  // Add leads without transitions (infer journey from current stage)
  for (const lead of filteredLeads) {
    if (!leadHistories.has(lead.id)) {
      const currentStageIndex = LEAD_STAGE_ORDER.indexOf(lead.stage as LeadStage);
      const inferredStages: string[] = [];
      const inferredTimestamps: string[] = [];
      const stageEntryTime: Record<string, string> = {};

      if (currentStageIndex >= 0) {
        for (let i = 0; i <= currentStageIndex; i++) {
          const stage = LEAD_STAGE_ORDER[i];
          if (stage) {
            inferredStages.push(stage);
            inferredTimestamps.push(lead.createdAt);
            stageEntryTime[stage] = lead.createdAt;
          }
        }
      } else {
        inferredStages.push(lead.stage);
        inferredTimestamps.push(lead.createdAt);
        stageEntryTime[lead.stage] = lead.createdAt;
      }

      leadHistories.set(lead.id, {
        stages: inferredStages,
        timestamps: inferredTimestamps,
        currentStage: lead.stage,
        stageEntryTime,
      });
    }
  }

  // =========================================================================
  // Calculate all analytics from the shared lead histories
  // =========================================================================

  // 1. Historical Funnel
  const stageMetrics: Record<string, {
    passedThrough: Set<string>;
    currentlyIn: number;
    exitedTo: Record<string, number>;
    totalDays: number;
    daysCount: number;
  }> = {};

  for (const stage of LEAD_STAGE_ORDER) {
    stageMetrics[stage] = {
      passedThrough: new Set(),
      currentlyIn: 0,
      exitedTo: {},
      totalDays: 0,
      daysCount: 0,
    };
  }

  // 2. Stage Velocity & Lead Aging preparation
  const velocityByStage: Record<string, { totalDays: number; count: number }> = {};
  // Historical aging: track time spent in each stage for ALL leads that passed through
  const historicalAgingByStage: Record<string, {
    total: number;
    leadIds: Set<string>;
    buckets: number[];
    bucketLeadIds: Set<string>[];
    daysSpent: number[];
  }> = {};
  const now = new Date();

  for (const stage of LEAD_STAGE_ORDER) {
    velocityByStage[stage] = { totalDays: 0, count: 0 };
    historicalAgingByStage[stage] = {
      total: 0,
      leadIds: new Set(),
      buckets: [0, 0, 0, 0],
      bucketLeadIds: [new Set(), new Set(), new Set(), new Set()],
      daysSpent: [],
    };
  }

  // Process each lead's history
  for (const [leadId, history] of leadHistories) {
    const { stages, timestamps, currentStage } = history;

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!stage) continue;

      // Initialize if not in standard order
      if (!stageMetrics[stage]) {
        stageMetrics[stage] = {
          passedThrough: new Set(),
          currentlyIn: 0,
          exitedTo: {},
          totalDays: 0,
          daysCount: 0,
        };
      }
      if (!velocityByStage[stage]) {
        velocityByStage[stage] = { totalDays: 0, count: 0 };
      }
      if (!historicalAgingByStage[stage]) {
        historicalAgingByStage[stage] = {
          total: 0,
          leadIds: new Set(),
          buckets: [0, 0, 0, 0],
          bucketLeadIds: [new Set(), new Set(), new Set(), new Set()],
          daysSpent: [],
        };
      }

      const metrics = stageMetrics[stage]!;
      metrics.passedThrough.add(leadId);

      // Calculate days in stage (for velocity and historical aging)
      const entryTimestamp = timestamps[i];
      if (entryTimestamp) {
        const entryDate = new Date(entryTimestamp);
        const exitTimestamp = timestamps[i + 1];
        const exitDate = exitTimestamp ? new Date(exitTimestamp) : now;
        const days = Math.max(0, Math.floor((exitDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24)));

        metrics.totalDays += days;
        metrics.daysCount += 1;

        // Also track for velocity
        velocityByStage[stage]!.totalDays += days;
        velocityByStage[stage]!.count += 1;

        // Track historical aging - time spent in this stage (for ALL leads that passed through)
        const agingData = historicalAgingByStage[stage];
        if (agingData) {
          agingData.total += 1;
          agingData.leadIds.add(leadId);
          agingData.daysSpent.push(days);
          // Find bucket for this duration
          for (let b = 0; b < AGING_BUCKETS.length; b++) {
            const bucket = AGING_BUCKETS[b];
            if (bucket && days >= bucket.min && days < bucket.max) {
              agingData.buckets[b] = (agingData.buckets[b] ?? 0) + 1;
              agingData.bucketLeadIds[b]?.add(leadId);
              break;
            }
          }
        }
      }

      // Track exitedTo
      if (i < stages.length - 1) {
        const nextStage = stages[i + 1];
        if (nextStage) {
          metrics.exitedTo[nextStage] = (metrics.exitedTo[nextStage] || 0) + 1;
        }
      }
    }

    // Count currently in stage
    if (currentStage && stageMetrics[currentStage]) {
      stageMetrics[currentStage]!.currentlyIn += 1;
    }
  }

  // Build funnel stages
  const funnelStages: HistoricalFunnelStage[] = [];
  for (const stage of LEAD_STAGE_ORDER) {
    const metrics = stageMetrics[stage];
    if (!metrics) continue;

    const passedThrough = metrics.passedThrough.size;
    const avgDays = metrics.daysCount > 0 ? Math.round(metrics.totalDays / metrics.daysCount) : 0;

    const nextStageIndex = LEAD_STAGE_ORDER.indexOf(stage as LeadStage) + 1;
    const nextStage = LEAD_STAGE_ORDER[nextStageIndex];
    const movedToNext = nextStage ? (metrics.exitedTo[nextStage] || 0) : 0;
    const conversionRate = passedThrough > 0 ? Math.round((movedToNext / passedThrough) * 100) : 0;
    const dropoffRate = passedThrough > 0
      ? Math.max(0, Math.round(((passedThrough - movedToNext - metrics.currentlyIn) / passedThrough) * 100))
      : 0;

    funnelStages.push({
      stage,
      passedThrough,
      passedThroughIds: Array.from(metrics.passedThrough),
      currentlyIn: metrics.currentlyIn,
      exitedTo: metrics.exitedTo,
      avgDaysInStage: avgDays,
      conversionRate,
      dropoffRate,
    });
  }

  const firstStageCount = funnelStages[0]?.passedThrough ?? 0;
  const lastStageCount = funnelStages[funnelStages.length - 1]?.passedThrough ?? 0;

  const funnel: HistoricalFunnelData = {
    stages: funnelStages,
    totalLeadsInPeriod: leadHistories.size,
    overallConversionRate: firstStageCount > 0 ? Math.round((lastStageCount / firstStageCount) * 100) : 0,
    leadsInProgress: funnelStages.reduce((sum, s) => sum + s.currentlyIn, 0),
  };

  // Build stage velocity
  const stageVelocity: StageVelocity[] = [];
  for (const stage of LEAD_STAGE_ORDER) {
    const data = velocityByStage[stage];
    if (data && data.count > 0) {
      stageVelocity.push({
        stage,
        avgDays: Math.round(data.totalDays / data.count),
        count: data.count,
      });
    }
  }

  // Build lead aging from historical data (shows all leads that passed through each stage)
  const leadAging: LeadAgingData[] = [];
  for (const stage of LEAD_STAGE_ORDER) {
    const data = historicalAgingByStage[stage];
    if (data && data.total > 0) {
      leadAging.push({
        stage,
        total: data.total,
        leadIds: Array.from(data.leadIds),
        buckets: AGING_BUCKETS.map((bucket, i) => ({
          ...bucket,
          count: data.buckets[i] ?? 0,
          leadIds: Array.from(data.bucketLeadIds[i] ?? new Set()),
        })),
      });
    }
  }

  // 3. Activity Trends - use actual transitions
  const days = startDate && endDate
    ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
    : 90;

  const trendStartDate = startDate ? new Date(startDate) : new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const dataByDate: Record<string, TrendDataPoint> = {};

  for (let i = 0; i < days; i++) {
    const date = new Date(trendStartDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split('T')[0] ?? '';
    if (dateStr) {
      dataByDate[dateStr] = {
        date: dateStr,
        newLeads: 0,
        stageChanges: 0,
        dealsCreated: 0,
      };
    }
  }

  // Count new leads by creation date
  for (const lead of filteredLeads) {
    const createdDate = lead.createdAt?.split('T')[0];
    if (createdDate && dataByDate[createdDate]) {
      dataByDate[createdDate]!.newLeads += 1;
    }
  }

  // Count actual stage changes from transitions
  for (const transition of filteredTransitions) {
    const transitionDate = transition.timestamp?.split('T')[0];
    if (transitionDate && dataByDate[transitionDate]) {
      dataByDate[transitionDate]!.stageChanges += 1;
    }
  }

  // Count deals by creation date
  for (const deal of filteredDeals) {
    const createdDate = deal.createdAt?.split('T')[0];
    if (createdDate && dataByDate[createdDate]) {
      dataByDate[createdDate]!.dealsCreated += 1;
    }
  }

  const activityTrends = Object.values(dataByDate).sort((a, b) => a.date.localeCompare(b.date));

  // 4. Lead to Deal Conversion (use existing function with filtered data)
  const leadToDealConversion = calculateLeadToDealConversion(filteredLeads, filteredDeals);

  return {
    funnel,
    stageVelocity,
    leadAging,
    activityTrends,
    leadToDealConversion,
    totalLeads: filteredLeads.length,
    totalTransitions: filteredTransitions.length,
    dateRange: startDate && endDate ? { start: startDate, end: endDate } : null,
  };
}

// ============================================================================
// Enhanced Analytics Functions
// ============================================================================

/**
 * Calculate weighted pipeline value
 * Formula: Σ(deal.value × deal.probability)
 */
export function calculateWeightedPipeline(deals: Deal[]): number {
  return deals.reduce((sum, deal) => {
    // Only include active deals (not closed lost)
    if (isLostStage(deal.stage)) return sum;
    const probability = (deal.probability || 0) / 100;
    return sum + (deal.value * probability);
  }, 0);
}

/**
 * Pipeline Coverage Ratio - Traditional sales metric
 *
 * Formula: Pipeline Value / Target (expressed as ratio, e.g., 3.2x)
 * Industry benchmark: 3-4x coverage is considered healthy
 *
 * @returns Coverage as a ratio (e.g., 3.2 means 3.2x coverage)
 */
export function calculatePipelineCoverage(deals: Deal[], target: number): {
  ratio: number; // Coverage ratio (e.g., 3.2 for 3.2x)
  percentage: number; // For backwards compatibility (e.g., 320)
  pipelineValue: number;
  target: number;
  isHealthy: boolean; // True if ratio >= 3x
} {
  const totalPipeline = deals
    .filter(d => !isLostStage(d.stage) && !isWonStage(d.stage))
    .reduce((sum, d) => sum + d.value, 0);

  if (target <= 0) {
    return {
      ratio: 0,
      percentage: 0,
      pipelineValue: totalPipeline,
      target: 0,
      isHealthy: false,
    };
  }

  const ratio = totalPipeline / target;

  return {
    ratio: Math.round(ratio * 10) / 10, // Round to 1 decimal (e.g., 3.2)
    percentage: Math.round(ratio * 100), // Backwards compatibility
    pipelineValue: totalPipeline,
    target,
    isHealthy: ratio >= 3, // Industry benchmark: 3x+ is healthy
  };
}

/**
 * Lead Velocity Rate (LVR) - Key B2B SaaS growth metric
 *
 * LVR measures the month-over-month growth rate of qualified leads.
 * It's a leading indicator of future revenue growth - a consistently positive
 * LVR suggests the pipeline is healthy and revenue growth is likely to follow.
 *
 * Formula: ((Qualified leads this month - Qualified leads last month) / Qualified leads last month) × 100
 *
 * Example: 100 qualified leads last month, 120 this month = 20% LVR
 *
 * @returns LVR percentage and period details
 */
export function calculateLeadVelocity(
  leads: Lead[],
  _deals: Deal[] = []
): {
  lvr: number; // Lead Velocity Rate as percentage
  rate: number; // Alias for lvr (backwards compatibility)
  thisMonth: number;
  lastMonth: number;
  currentPeriod: number;
  previousPeriod: number;
} {
  const now = new Date();

  // Current month and previous month periods
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

  // Qualified stages (further down the funnel)
  const qualifiedStages = ['Qualified', 'Project Identification', 'Account Qualification'];

  // Current month qualified leads
  const thisMonthQualified = leads.filter(lead => {
    const created = new Date(lead.createdAt);
    return created >= thisMonthStart && qualifiedStages.includes(lead.stage);
  }).length;

  // Previous month qualified leads
  const lastMonthQualified = leads.filter(lead => {
    const created = new Date(lead.createdAt);
    return created >= lastMonthStart && created <= lastMonthEnd && qualifiedStages.includes(lead.stage);
  }).length;

  // LVR Formula: ((This month - Last month) / Last month) × 100
  const lvr = lastMonthQualified > 0
    ? Math.round(((thisMonthQualified - lastMonthQualified) / lastMonthQualified) * 100)
    : thisMonthQualified > 0 ? 100 : 0;

  return {
    lvr,
    rate: lvr, // Alias for backwards compatibility
    thisMonth: thisMonthQualified,
    lastMonth: lastMonthQualified,
    currentPeriod: thisMonthQualified,
    previousPeriod: lastMonthQualified,
  };
}

/**
 * Win/Loss analysis data
 */
export interface WinLossData {
  wonCount: number;
  lostCount: number;
  winRate: number;
  totalWonValue: number;
  totalLostValue: number;
  avgWonDealSize: number;
  avgLostDealSize: number;
  wonDeals: Deal[];
  lostDeals: Deal[];
}

/**
 * Check if a stage name indicates a "won" deal
 * Handles various naming conventions from Monday.com boards
 */
export function isWonStage(stage: string | undefined): boolean {
  if (!stage) return false;
  const normalized = stage.toLowerCase().trim();
  return (
    normalized === 'closed won' ||
    normalized === 'won' ||
    normalized === 'deal won' ||
    normalized === 'closed-won' ||
    normalized === 'closed/won' ||
    normalized.includes('won') && !normalized.includes('lost')
  );
}

/**
 * Check if a stage name indicates a "lost" deal
 * Handles various naming conventions from Monday.com boards
 */
export function isLostStage(stage: string | undefined): boolean {
  if (!stage) return false;
  const normalized = stage.toLowerCase().trim();
  return (
    normalized === 'closed lost' ||
    normalized === 'lost' ||
    normalized === 'deal lost' ||
    normalized === 'closed-lost' ||
    normalized === 'closed/lost' ||
    normalized.includes('lost') && !normalized.includes('won')
  );
}

/**
 * Calculate win/loss analysis for closed deals
 */
export function calculateWinLossAnalysis(deals: Deal[]): WinLossData {
  const wonDeals = deals.filter(d => isWonStage(d.stage));
  const lostDeals = deals.filter(d => isLostStage(d.stage));

  const wonCount = wonDeals.length;
  const lostCount = lostDeals.length;
  const totalClosed = wonCount + lostCount;

  const totalWonValue = wonDeals.reduce((sum, d) => sum + d.value, 0);
  const totalLostValue = lostDeals.reduce((sum, d) => sum + d.value, 0);

  return {
    wonCount,
    lostCount,
    winRate: totalClosed > 0 ? Math.round((wonCount / totalClosed) * 100) : 0,
    totalWonValue,
    totalLostValue,
    avgWonDealSize: wonCount > 0 ? Math.round(totalWonValue / wonCount) : 0,
    avgLostDealSize: lostCount > 0 ? Math.round(totalLostValue / lostCount) : 0,
    wonDeals,
    lostDeals,
  };
}

/**
 * Calculate average response time (days from lead creation to first activity)
 */
export function calculateAvgResponseTime(leads: Lead[]): number {
  const leadsWithActivity = leads.filter(l => l.lastActivity && l.createdAt);
  if (leadsWithActivity.length === 0) return 0;

  const totalDays = leadsWithActivity.reduce((sum, lead) => {
    const created = new Date(lead.createdAt);
    const firstActivity = new Date(lead.lastActivity!);
    const days = Math.max(0, Math.floor((firstActivity.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));
    return sum + days;
  }, 0);

  return Math.round(totalDays / leadsWithActivity.length);
}

/**
 * Group items by a property
 */
export function groupBy<T>(items: T[], getKey: (item: T) => string | undefined): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  for (const item of items) {
    const key = getKey(item) || 'Unknown';
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(item);
  }
  return grouped;
}

/**
 * Group leads by source
 */
export function groupLeadsBySource(leads: Lead[]): Record<string, Lead[]> {
  return groupBy(leads, (lead) => lead.source);
}

/**
 * Group leads by owner
 * Handles comma-separated owner names by attributing the lead to each owner
 */
export function groupLeadsByOwner(leads: Lead[]): Record<string, Lead[]> {
  const grouped: Record<string, Lead[]> = {};

  for (const lead of leads) {
    const ownerField = lead.owner || 'Unknown';
    // Split by comma to handle multiple owners (e.g., "Jake Evans, Gabrielle Fisher")
    const owners = ownerField.includes(',')
      ? ownerField.split(',').map(o => o.trim()).filter(o => o.length > 0)
      : [ownerField];

    for (const owner of owners) {
      if (!grouped[owner]) {
        grouped[owner] = [];
      }
      grouped[owner].push(lead);
    }
  }

  return grouped;
}

/**
 * Group deals by owner
 */
export function groupDealsByOwner(deals: Deal[]): Record<string, Deal[]> {
  return groupBy(deals, (deal) => deal.owner);
}

/**
 * Rep performance data
 */
export interface RepPerformance {
  name: string;
  totalLeads: number;
  convertedLeads: number;
  conversionRate: number;
  totalDeals: number;
  totalPipeline: number;
  wonDeals: number;
  wonValue: number;
  avgDealSize: number;
  avgResponseTime: number;
}

/**
 * Calculate performance metrics per rep/owner
 * Attribution is based on lead ownership - deals are credited to the rep who owned the lead
 */
export function calculateRepPerformance(
  leads: Lead[],
  _deals: Deal[],
  leadToDealConversion: LeadToDealConversion
): RepPerformance[] {
  const leadsByOwner = groupLeadsByOwner(leads);

  // Get unique owners from leads only (we attribute deals based on lead ownership)
  const allOwners = new Set(Object.keys(leadsByOwner));

  const repPerformance: RepPerformance[] = [];

  for (const owner of allOwners) {
    const ownerLeads = leadsByOwner[owner] || [];

    // Find converted leads for this owner and their associated deals
    // Handle comma-separated owners by checking if the owner is included
    const ownerConversions = leadToDealConversion.convertedLeads
      .filter(c => {
        const leadOwner = c.lead.owner || '';
        if (leadOwner.includes(',')) {
          return leadOwner.split(',').map(o => o.trim()).includes(owner);
        }
        return leadOwner === owner;
      });

    const convertedLeadIds = new Set(ownerConversions.map(c => c.lead.id));

    // Get deals from this rep's converted leads (attribution by lead ownership)
    const dealsFromOwnerLeads = ownerConversions.map(c => c.deal);

    // Won deals from this rep's leads
    const wonDeals = dealsFromOwnerLeads.filter(d => isWonStage(d.stage));

    // Active pipeline from this rep's leads (deals not yet won/lost)
    const activePipeline = dealsFromOwnerLeads.filter(d =>
      !isWonStage(d.stage) && !isLostStage(d.stage)
    );

    repPerformance.push({
      name: owner,
      totalLeads: ownerLeads.length,
      convertedLeads: convertedLeadIds.size,
      conversionRate: ownerLeads.length > 0
        ? Math.round((convertedLeadIds.size / ownerLeads.length) * 100)
        : 0,
      totalDeals: dealsFromOwnerLeads.length,
      totalPipeline: activePipeline.reduce((sum, d) => sum + d.value, 0),
      wonDeals: wonDeals.length,
      wonValue: wonDeals.reduce((sum, d) => sum + d.value, 0),
      avgDealSize: wonDeals.length > 0
        ? Math.round(wonDeals.reduce((sum, d) => sum + d.value, 0) / wonDeals.length)
        : 0,
      avgResponseTime: calculateAvgResponseTime(ownerLeads),
    });
  }

  // Sort by won value descending
  return repPerformance.sort((a, b) => b.wonValue - a.wonValue);
}

/**
 * Source attribution data
 */
export interface SourceAttribution {
  source: string;
  leadCount: number;
  convertedCount: number;
  conversionRate: number;
  pipelineValue: number;
  wonValue: number;
}

/**
 * Calculate lead source attribution metrics
 */
export function calculateSourceAttribution(
  leads: Lead[],
  _deals: Deal[],
  leadToDealConversion: LeadToDealConversion
): SourceAttribution[] {
  const leadsBySource = groupLeadsBySource(leads);

  // Map converted leads to their source
  const convertedBySource: Record<string, Set<string>> = {};
  const dealValueBySource: Record<string, { pipeline: number; won: number }> = {};

  for (const converted of leadToDealConversion.convertedLeads) {
    const source = converted.lead.source || 'Unknown';

    if (!convertedBySource[source]) {
      convertedBySource[source] = new Set();
      dealValueBySource[source] = { pipeline: 0, won: 0 };
    }

    const sourceSet = convertedBySource[source]!;
    const sourceValue = dealValueBySource[source]!;

    sourceSet.add(converted.lead.id);

    if (isWonStage(converted.deal.stage)) {
      sourceValue.won += converted.deal.value;
    } else if (!isLostStage(converted.deal.stage)) {
      sourceValue.pipeline += converted.deal.value;
    }
  }

  const sourceAttribution: SourceAttribution[] = [];

  for (const [source, sourceLeads] of Object.entries(leadsBySource)) {
    const convertedCount = convertedBySource[source]?.size || 0;
    const values = dealValueBySource[source] || { pipeline: 0, won: 0 };

    sourceAttribution.push({
      source,
      leadCount: sourceLeads.length,
      convertedCount,
      conversionRate: sourceLeads.length > 0
        ? Math.round((convertedCount / sourceLeads.length) * 100)
        : 0,
      pipelineValue: values.pipeline,
      wonValue: values.won,
    });
  }

  // Sort by lead count descending
  return sourceAttribution.sort((a, b) => b.leadCount - a.leadCount);
}

/**
 * Forecast vs Actual data point
 */
export interface ForecastDataPoint {
  period: string;
  forecast: number;
  actual: number;
  accuracy: number;
}

/**
 * Calculate forecast vs actual closed deals by month
 * Forecast is based on deals' close dates and probability
 */
export function calculateForecastVsActual(deals: Deal[], months: number = 6): ForecastDataPoint[] {
  const now = new Date();
  const results: ForecastDataPoint[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const periodKey = monthDate.toISOString().slice(0, 7); // YYYY-MM

    // Actual: Closed Won deals in this month
    const actualDeals = deals.filter(d => {
      if (!isWonStage(d.stage)) return false;
      const closeDate = d.closeDate ? new Date(d.closeDate) : new Date(d.updatedAt || d.createdAt);
      return closeDate >= monthDate && closeDate <= monthEnd;
    });
    const actual = actualDeals.reduce((sum, d) => sum + d.value, 0);

    // Forecast: Deals that had this month as expected close date × probability
    // For past months, use deals that were open at that time
    const forecastDeals = deals.filter(d => {
      if (!d.closeDate) return false;
      const expectedClose = new Date(d.closeDate);
      return expectedClose >= monthDate && expectedClose <= monthEnd;
    });
    const forecast = forecastDeals.reduce((sum, d) => {
      return sum + (d.value * (d.probability || 50) / 100);
    }, 0);

    const accuracy = forecast > 0 ? Math.round((actual / forecast) * 100) : (actual > 0 ? 100 : 0);

    results.push({
      period: periodKey,
      forecast: Math.round(forecast),
      actual: Math.round(actual),
      accuracy: Math.min(accuracy, 200), // Cap at 200% to handle outliers
    });
  }

  return results;
}

/**
 * Deal slippage data
 */
export interface DealSlippage {
  deal: Deal;
  originalCloseDate: string;
  currentCloseDate: string;
  daysSlipped: number;
}

/**
 * Identify deals that have slipped (close date pushed back)
 * Note: This requires historical close date tracking which may not be available
 * For now, we identify deals past their close date that aren't closed
 */
export function identifySlippedDeals(deals: Deal[]): Deal[] {
  const now = new Date();

  return deals.filter(deal => {
    if (isWonStage(deal.stage) || isLostStage(deal.stage)) return false;
    if (!deal.closeDate) return false;

    const closeDate = new Date(deal.closeDate);
    return closeDate < now;
  }).sort((a, b) => {
    // Sort by how far past close date
    const aClose = new Date(a.closeDate!);
    const bClose = new Date(b.closeDate!);
    return aClose.getTime() - bClose.getTime();
  });
}
